import { PosState } from "@unleashed-admin/stores/PosSlice";
import {
  CartItem,
  PrePurchasedItem,
  ProductModifierViewModel,
  ProductOptionViewModel,
} from "../models";
import { isSameProduct, findSimilarProduct } from "./Product";

export const noneProductModifier: ProductModifierViewModel = {
  id: 0,
  name: "None",
  price: 0,
  sortOrder: 0,
  isActive: true,
};

export const areModifiersEqual = (
  currentProductModifiers: ProductModifierViewModel[],
  productModifiersToCheck: ProductModifierViewModel[]
): boolean => {
  if (
    !currentProductModifiers ||
    !productModifiersToCheck ||
    currentProductModifiers.length !== productModifiersToCheck.length
  ) {
    return false;
  }

  // Check if all product modifiers are equal irrespective of the order
  return (
    currentProductModifiers.every(
      (currentProductModifier: ProductModifierViewModel) => {
        return productModifiersToCheck.some(
          (productModifierToCheck: ProductModifierViewModel) =>
            currentProductModifier.id === productModifierToCheck.id
        );
      }
    ) &&
    productModifiersToCheck.every(
      (productModifierToCheck: ProductModifierViewModel) => {
        return currentProductModifiers.some(
          (currentProductModifier: ProductModifierViewModel) =>
            productModifierToCheck.id === currentProductModifier.id
        );
      }
    )
  );
};

export const areProductOptionsEqual = (
  currentProductOptions: ProductOptionViewModel[],
  productOptionsToCheck: ProductOptionViewModel[]
): boolean => {
  if (
    !currentProductOptions ||
    !productOptionsToCheck ||
    currentProductOptions.length !== productOptionsToCheck.length
  ) {
    return false;
  }

  // Check if all product options are equal irrespective of the order
  return (
    currentProductOptions.every(
      (currentProductOption: ProductOptionViewModel) => {
        return productOptionsToCheck.some(
          (productOptionToCheck: ProductOptionViewModel) => {
            return (
              currentProductOption.id === productOptionToCheck.id &&
              areModifiersEqual(
                currentProductOption.productModifiers ?? [],
                productOptionToCheck.productModifiers ?? []
              )
            );
          }
        );
      }
    ) &&
    productOptionsToCheck.every(
      (productOptionToCheck: ProductOptionViewModel) => {
        return currentProductOptions.some(
          (currentProductOption: ProductOptionViewModel) => {
            return (
              productOptionToCheck.id === currentProductOption.id &&
              areModifiersEqual(
                productOptionToCheck.productModifiers ?? [],
                currentProductOption.productModifiers ?? []
              )
            );
          }
        );
      }
    )
  );
};

/* Function returns sorted productOptions */
export const sortProductOptions = (
  productOptions?: ProductOptionViewModel[]
) => {
  return [...(productOptions || [])]
    .sort((a: ProductOptionViewModel, b: ProductOptionViewModel) => {
      if (a.sortOrder === undefined && b.sortOrder !== undefined) {
        return 1;
      }
      if (a.sortOrder !== undefined && b.sortOrder === undefined) {
        return -1;
      }
      if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
        return a.sortOrder - b.sortOrder;
      }
      return 0;
    })
    .map((productOption: ProductOptionViewModel) => {
      const sortedProductModifiers = [
        ...(productOption.productModifiers || []),
      ].sort((a: ProductModifierViewModel, b: ProductModifierViewModel) => {
        if (a.sortOrder === undefined && b.sortOrder !== undefined) {
          return 1;
        }
        if (a.sortOrder !== undefined && b.sortOrder === undefined) {
          return -1;
        }
        if (a.sortOrder !== undefined && b.sortOrder !== undefined) {
          return a.sortOrder - b.sortOrder;
        }
        return 0;
      });
      return {
        ...productOption,
        productModifiers: sortedProductModifiers,
      };
    });
};

export const transformProductOptions = (
  productOptions: ProductOptionViewModel[] = []
) => {
  return productOptions.map((option: ProductOptionViewModel) => ({
    ...option,
    multiSelectMaxQuantity: option.multiSelectMaxQuantity ?? 1, // Default to 1 if not set
    productModifiers: option.isRequired
      ? option.productModifiers
      : [noneProductModifier, ...(option.productModifiers ?? [])],
  }));
};

export const getTotalPriceForProductOptions = (
  options: ProductOptionViewModel[]
): number => {
  let optionPrice = 0;
  options?.forEach((option: ProductOptionViewModel) => {
    option.productModifiers?.forEach((modifier: ProductModifierViewModel) => {
      optionPrice += modifier.price || 0;
    });
  });
  return optionPrice;
};

export const filterNoneProductModifier = (
  productModifiers?: ProductModifierViewModel[]
) => {
  return (
    productModifiers?.filter(
      (modifier: ProductModifierViewModel) => modifier.name !== "None"
    ) ?? []
  );
};

export const removeOptionalModifierFromRedeemingItem = (
  state: PosState,
  redeemItem: CartItem,
  productModifierId: number
) => {
  const existingProducts = state.itemsForRedemption.filter(
    (item: PrePurchasedItem) => isSameProduct(item, redeemItem)
  );

  if (existingProducts.length === 0) return;

  const updatedProducts = existingProducts.map((item: PrePurchasedItem) => {
    return {
      ...item,
      productOptions: item.productOptions
        ?.map((o: ProductOptionViewModel) => ({
          ...o,
          productModifiers: o.productModifiers?.filter(
            (m: ProductModifierViewModel) => m.id !== productModifierId
          ),
        }))
        .filter((o: ProductOptionViewModel) => o.productModifiers?.length),
    };
  });

  const existingProductWithUpdatedModifiers = findSimilarProduct(
    state.itemsForRedemption,
    updatedProducts[0]
  ) as PrePurchasedItem;

  if (existingProductWithUpdatedModifiers) {
    // Remove the old product and update the quantity of the existing one
    state.itemsForRedemption = state.itemsForRedemption.filter(
      (item: PrePurchasedItem) => !isSameProduct(item, existingProducts[0])
    );
    existingProductWithUpdatedModifiers.quantity += redeemItem.quantity;
  } else {
    // Update existing product options
    updatedProducts.forEach((item: PrePurchasedItem) => {
      const existingProduct = state.itemsForRedemption.find(
        (i: PrePurchasedItem) => i.uuid === item.uuid
      );
      if (existingProduct) {
        existingProduct.productOptions = item.productOptions;
      }
    });
  }
};

export const removeOptionalModifierFromCartItem = (
  state: PosState,
  cartItem: CartItem,
  productModifierId: number
) => {
  state.itemsInCart = state.itemsInCart ?? [];

  const existingProduct = findSimilarProduct(
    state.itemsInCart,
    cartItem
  ) as CartItem;

  if (!existingProduct?.productOptions) return;

  const updatedProductOptions = existingProduct.productOptions
    .map((o: ProductOptionViewModel) => ({
      ...o,
      productModifiers: o.productModifiers?.filter(
        (m: ProductModifierViewModel) => m.id !== productModifierId
      ),
    }))
    .filter((o: ProductOptionViewModel) => o.productModifiers?.length);

  const updatedCartItem = {
    ...existingProduct,
    productOptions: updatedProductOptions,
  };

  const existingProductWithUpdatedModifiers = findSimilarProduct(
    state.itemsInCart,
    updatedCartItem
  ) as CartItem;

  if (existingProductWithUpdatedModifiers) {
    // Remove the old product and update the quantity of the existing one
    state.itemsInCart = state.itemsInCart.filter(
      (item: CartItem) => !isSameProduct(item, existingProduct)
    );
    existingProductWithUpdatedModifiers.quantity += existingProduct.quantity;
  } else {
    // Update existing product options
    existingProduct.productOptions = updatedProductOptions;

    if (existingProduct.productOptions.length === 0) {
      state.itemsInCart = state.itemsInCart.filter(
        (item: CartItem) => item !== existingProduct
      );
    }
  }
};

export const removeOptionalModifierFromComboCartItem = (
  state: PosState,
  cartItem: CartItem,
  productModifierId: number,
  productId: number
) => {
  state.itemsInCart = state.itemsInCart ?? [];

  const existingProduct = findSimilarProduct(
    state.itemsInCart,
    cartItem
  ) as CartItem;

  if (!existingProduct?.comboConfigs) return;

  const updatedComboConfig = existingProduct.comboConfigs.map(
    (comboConfig) => ({
      ...comboConfig,
      comboItemProducts: comboConfig.comboItemProducts?.map((product) => {
        if (product.productId === productId) {
          return {
            ...product,
            productOptions: product.productOptions
              ?.map((o: ProductOptionViewModel) => ({
                ...o,
                productModifiers: o.productModifiers?.filter(
                  (m: ProductModifierViewModel) => m.id !== productModifierId
                ),
              }))
              .filter(
                (o: ProductOptionViewModel) => o.productModifiers?.length
              ),
          };
        }
        return product;
      }),
    })
  );

  const updatedCartItem = {
    ...existingProduct,
    comboConfigs: updatedComboConfig,
  };

  const existingProductWithUpdatedComboConfig = findSimilarProduct(
    state.itemsInCart,
    updatedCartItem
  ) as CartItem;

  if (existingProductWithUpdatedComboConfig) {
    // Remove the old product and update the quantity of the existing one
    state.itemsInCart = state.itemsInCart.filter(
      (item: CartItem) => !isSameProduct(item, existingProduct)
    );
    existingProductWithUpdatedComboConfig.quantity += existingProduct.quantity;
  } else {
    // Update existing product options
    existingProduct.comboConfigs = updatedComboConfig;

    if (existingProduct.comboConfigs.length === 0) {
      state.itemsInCart = state.itemsInCart.filter(
        (item: CartItem) => item !== existingProduct
      );
    }
  }
};
